import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getRunApp, type AppLocale } from '@/api/aso';
import { Modal } from '@/components/Modal';
import { AssetImage } from './AssetImage';

/** One competitor in one run: every locale we read, with its text and graphics. */
export function AppModal({ runId, appId, onClose }: { runId: number; appId: number; onClose: () => void }) {
  const q = useQuery({ queryKey: ['aso-run-app', runId, appId], queryFn: () => getRunApp(runId, appId) });
  const locales = q.data?.locales ?? [];
  const [picked, setPicked] = useState<string | null>(null);
  const current: AppLocale | undefined = locales.find((l) => l.locale === picked) ?? locales.find((l) => l.available) ?? locales[0];
  const app = q.data?.app;

  return (
    <Modal isOpen onClose={onClose} title={app?.name || app?.store_id || 'App'} size="5xl">
      {q.isLoading && <div className="p-6 text-center text-content-subtle">Loading…</div>}
      {app && (
        <div className="space-y-4">
          <div className="flex items-start gap-4">
            <AssetImage hash={app.icon_hash} alt={app.name ?? app.store_id} className="w-16 h-16 rounded-xl object-cover shrink-0" />
            <div className="min-w-0 text-sm">
              <div className="font-medium text-content">
                #{app.rank} · {app.name ?? '—'}
                {app.is_ours === 1 && <span className="ml-2 inline-flex px-1.5 py-0.5 rounded bg-primary/10 text-primary text-xs">ours</span>}
              </div>
              <div className="text-content-muted">{app.developer ?? '—'} · {app.category ?? '—'}</div>
              <div className="text-content-subtle text-xs">
                {app.rating != null ? `★ ${app.rating}` : ''} {app.installs ? `· ${app.installs}` : ''} ·{' '}
                <a className="hover:underline" href={`https://play.google.com/store/apps/details?id=${app.store_id}`} target="_blank" rel="noreferrer">
                  {app.store_id}
                </a>
              </div>
            </div>
          </div>

          <div className="flex flex-wrap gap-1">
            {locales.map((l) => (
              <button
                key={l.locale}
                onClick={() => setPicked(l.locale)}
                className={`px-2 py-0.5 rounded text-xs border ${
                  current?.locale === l.locale ? 'bg-primary text-white border-primary' : 'border-border text-content-muted hover:bg-surface-muted'
                } ${l.available ? '' : 'line-through opacity-60'}`}
                title={l.available ? `${l.locale} · gl=${l.country}` : l.error ?? 'not available'}
              >
                {l.locale}
              </button>
            ))}
          </div>

          {current && !current.available && (
            <div className="text-sm text-content-muted">Not available in {current.locale}: {current.error ?? 'unknown'}</div>
          )}
          {current && current.available === 1 && (
            <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)] gap-4">
              <div className="space-y-3 text-sm">
                <Field label="Title" value={current.title} limit={30} />
                <Field label="Short description" value={current.short_desc} limit={80} />
                <Field label="Full description" value={current.full_desc} limit={4000} tall />
                {current.video_url && (
                  <div>
                    <div className="label">Video</div>
                    <a className="text-primary hover:underline break-all text-xs" href={current.video_url} target="_blank" rel="noreferrer">{current.video_url}</a>
                  </div>
                )}
              </div>
              <div className="space-y-3">
                {current.feature_hash && (
                  <div>
                    <div className="label mb-1">Feature graphic</div>
                    <AssetImage hash={current.feature_hash} alt="feature graphic" className="w-full max-w-md rounded-lg object-cover" />
                  </div>
                )}
                <div>
                  <div className="label mb-1">Screenshots ({current.screenshots.length})</div>
                  <div className="flex gap-2 overflow-x-auto pb-2">
                    {current.screenshots.map((s) => (
                      <div key={s.hash} className="shrink-0 w-28">
                        <AssetImage hash={s.hash} alt={`screenshot ${s.pos}`} className="w-28 rounded-md border border-border object-cover" />
                        <div className="text-[10px] text-content-subtle text-center mt-0.5">#{s.pos}</div>
                      </div>
                    ))}
                    {current.screenshots.length === 0 && <div className="text-xs text-content-subtle">none</div>}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </Modal>
  );
}

function Field({ label, value, limit, tall }: { label: string; value: string | null; limit: number; tall?: boolean }) {
  const len = value?.length ?? 0;
  return (
    <div>
      <div className="flex items-baseline justify-between">
        <span className="label">{label}</span>
        <span className={`text-[10px] ${len > limit ? 'text-danger' : 'text-content-subtle'}`}>{len}/{limit}</span>
      </div>
      <div className={`mt-1 whitespace-pre-wrap break-words rounded-md bg-surface-muted p-2 text-content ${tall ? 'max-h-72 overflow-y-auto text-xs' : ''}`}>
        {value || <span className="text-content-subtle">—</span>}
      </div>
    </div>
  );
}
